/**
 * M4S-5777 (X87, X88; PRD decision 7) — how far weights may go, and what a weighting is flagged
 * for. Pure. The one home of every weighting threshold: `weight-limits.guard.test.ts` fails on a
 * second copy of any of them in the crosstab code.
 *
 * Decision 7 ("As built"): the 5× cap stays (no weight above five times its group's mean weight;
 * rake and cell trim to it), and a scheme is FLAGGED, never trimmed further, when a weight falls
 * outside the house limits (0.3 to 3.0 times the mean) or its design effect is above 1.5. A kept
 * target set (X88, `weight-target-sets.ts`) may carry its own house limits; the cap is not a
 * limit a set can move.
 *
 * Every figure here is about the weighted GROUP (X85): the respondents a scheme weights, not the
 * whole table, so a scheme that weights Unexposed to match Exposed is judged on Unexposed's weights.
 */

/** No weight may exceed this many times its group's mean weight; rake and cell trim to it. */
export const WEIGHT_CAP_RATIO = 5;

/** The house limits (Research's Gen Pop standard, PRD decision 7). */
export const HOUSE_MIN_WEIGHT = 0.3;
export const HOUSE_MAX_WEIGHT = 3.0;
export const HOUSE_MAX_DEFF = 1.5;

/** What a scheme is flagged against. Weights are read as multiples of the group's mean weight. */
export interface WeightLimits {
    minWeight: number;
    maxWeight: number;
    maxDeff: number;
}

export const HOUSE_LIMITS: WeightLimits = Object.freeze({
    minWeight: HOUSE_MIN_WEIGHT,
    maxWeight: HOUSE_MAX_WEIGHT,
    maxDeff: HOUSE_MAX_DEFF,
});

const isRecord = (x: unknown): x is Record<string, unknown> =>
    typeof x === 'object' && x !== null && !Array.isArray(x);

const finite = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

/**
 * Limits read from JSON (a scheme, a kept target set), or null when they are not usable: each
 * must be a number, `0 < minWeight < 1 < maxWeight`, and `maxDeff >= 1`. Built in one key order so
 * two equal limits compare alike as JSON. A maximum weight above the cap is allowed and simply
 * never fires (the cap holds weights below it).
 */
export function parseWeightLimits(raw: unknown): WeightLimits | null {
    if (!isRecord(raw)) return null;
    const { minWeight, maxWeight, maxDeff } = raw;
    if (!finite(minWeight) || !finite(maxWeight) || !finite(maxDeff)) return null;
    if (!(minWeight > 0 && minWeight < 1 && maxWeight > 1 && maxDeff >= 1)) return null;
    return { minWeight, maxWeight, maxDeff };
}

/** Whether two limits are the house limits (so a disclosure need not repeat them). */
export function isHouseLimits(limits: WeightLimits): boolean {
    return (
        limits.minWeight === HOUSE_MIN_WEIGHT &&
        limits.maxWeight === HOUSE_MAX_WEIGHT &&
        limits.maxDeff === HOUSE_MAX_DEFF
    );
}

/** The weighted group's weights, summed up. */
export interface WeightStats {
    /** Respondents weighted (the group's shown respondents). */
    n: number;
    /** Kish's effective sample size, (Σw)² / Σw². */
    neff: number;
    /** Design effect, n / neff (1 when every weight is equal). */
    deff: number;
    /** Weighting efficiency, neff / n, between 0 and 1. */
    efficiency: number;
    /** The smallest and largest weight, as multiples of the group's mean weight. */
    minRatio: number;
    maxRatio: number;
    /** How many weights sit below `minWeight` and above `maxWeight` times the mean. */
    belowMin: number;
    aboveMax: number;
    /** How many weights sit at the cap (`WEIGHT_CAP_RATIO` times the mean). */
    capped: number;
    /** Respondents a weight could not be read for (the weights-from-a-variable method). */
    missing: number;
}

const EMPTY_STATS: WeightStats = {
    n: 0,
    neff: 0,
    deff: 1,
    efficiency: 1,
    minRatio: 1,
    maxRatio: 1,
    belowMin: 0,
    aboveMax: 0,
    capped: 0,
    missing: 0,
};

/**
 * The stats of one group's weights. A weight of 0 (a respondent whose weight could not be read)
 * is counted in `missing` and left out of the ratios, but stays in `n`: the respondent is shown,
 * and the effective base is what the zero costs.
 */
export function weightStats(
    weights: readonly number[],
    limits: WeightLimits = HOUSE_LIMITS,
    opts: { missing?: number; capRatio?: number } = {},
): WeightStats {
    const n = weights.length;
    if (n === 0) return { ...EMPTY_STATS, missing: opts.missing ?? 0 };
    let sum = 0;
    let sum2 = 0;
    let positive = 0;
    for (const w of weights) {
        sum += w;
        sum2 += w * w;
        if (w > 0) positive++;
    }
    const neff = sum2 > 0 ? (sum * sum) / sum2 : 0;
    const mean = positive > 0 ? sum / positive : 0;
    let minRatio = Infinity;
    let maxRatio = 0;
    let belowMin = 0;
    let aboveMax = 0;
    let capped = 0;
    const capRatio = opts.capRatio ?? WEIGHT_CAP_RATIO;
    // A little room, so a weight trimmed to exactly the cap, then rescaled, still counts as capped.
    const EPS = 1e-9;
    for (const w of weights) {
        if (!(w > 0) || !(mean > 0)) continue;
        const r = w / mean;
        if (r < minRatio) minRatio = r;
        if (r > maxRatio) maxRatio = r;
        if (r < limits.minWeight - EPS) belowMin++;
        if (r > limits.maxWeight + EPS) aboveMax++;
        if (r >= capRatio - 1e-6) capped++;
    }
    return {
        n,
        neff,
        deff: neff > 0 ? n / neff : 1,
        efficiency: n > 0 ? neff / n : 1,
        minRatio: Number.isFinite(minRatio) ? minRatio : 1,
        maxRatio: maxRatio || 1,
        belowMin,
        aboveMax,
        capped,
        missing: opts.missing ?? weights.filter((w) => !(w > 0)).length,
    };
}

export type WeightFlagKind =
    | 'deff'
    | 'low-weights'
    | 'high-weights'
    | 'capped'
    | 'missing'
    /** X85: respondents in both the weighted group and the group it matches (raking.ts). */
    | 'shared';

export interface WeightFlag {
    kind: WeightFlagKind;
    /** What the workspace and every file say about it. */
    message: string;
}

const plural = (n: number, one: string, many: string) =>
    `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

const fmt = (x: number, digits: number) => x.toFixed(digits);

/**
 * What a weighting is flagged for, in a fixed order (`stats` counted
 * against the same `limits`, as `weightWithin` counts them): the design effect, weights below and above
 * the limits, weights held at the cap, and respondents with no weight. Empty when nothing is out
 * of line. Flags only: nothing here changes a weight.
 */
export function weightFlags(stats: WeightStats, limits: WeightLimits = HOUSE_LIMITS): WeightFlag[] {
    if (stats.n === 0) return [];
    const out: WeightFlag[] = [];
    if (stats.deff > limits.maxDeff + 1e-9) {
        out.push({
            kind: 'deff',
            message: `Design effect ${fmt(stats.deff, 2)} is above the limit of ${fmt(limits.maxDeff, 1)}: the weighted group counts as ${Math.round(stats.neff).toLocaleString('en-US')} respondents for significance, not ${stats.n.toLocaleString('en-US')}.`,
        });
    }
    if (stats.belowMin > 0) {
        out.push({
            kind: 'low-weights',
            message: `${plural(stats.belowMin, 'weight is', 'weights are')} below ${fmt(limits.minWeight, 1)}× the mean (lowest ${fmt(stats.minRatio, 2)}×).`,
        });
    }
    if (stats.aboveMax > 0) {
        out.push({
            kind: 'high-weights',
            message: `${plural(stats.aboveMax, 'weight is', 'weights are')} above ${fmt(limits.maxWeight, 1)}× the mean (highest ${fmt(stats.maxRatio, 2)}×).`,
        });
    }
    if (stats.capped > 0) {
        out.push({
            kind: 'capped',
            message: `${plural(stats.capped, 'weight was', 'weights were')} capped at ${WEIGHT_CAP_RATIO}× the mean, so the weighted group may not match its targets exactly.`,
        });
    }
    if (stats.missing > 0) {
        out.push({
            kind: 'missing',
            message: `${plural(stats.missing, 'respondent has', 'respondents have')} no usable weight and count for nothing in the weighted figures.`,
        });
    }
    return out;
}

/** "Weighting efficiency 72% (design effect 1.39, effective base 412 of 572)". */
export function efficiencyLine(stats: WeightStats): string {
    return `Weighting efficiency ${Math.round(stats.efficiency * 100)}% (design effect ${fmt(stats.deff, 2)}, effective base ${Math.round(stats.neff).toLocaleString('en-US')} of ${stats.n.toLocaleString('en-US')})`;
}

/** "Limits: weights 0.3–3.0× the mean, design effect at most 1.5; weights capped at 5× the mean". */
export function limitsLine(limits: WeightLimits = HOUSE_LIMITS): string {
    return `Limits: weights ${fmt(limits.minWeight, 1)}–${fmt(limits.maxWeight, 1)}× the mean, design effect at most ${fmt(limits.maxDeff, 1)}; weights capped at ${WEIGHT_CAP_RATIO}× the mean`;
}
